import type { QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

/**
 * Empresa em foco no Dashboard executivo (ADR 0100): vale para esta aba, em todas as telas de
 * consulta, até a pessoa voltar para todas as empresas. Quem filtra é o servidor; aqui fica só a
 * escolha, que a faixa no topo mantém à vista.
 */
export interface FocusEntity {
  id: string;
  name: string;
}

const STORAGE_KEY = 'loot.focusEntity';
const listeners = new Set<() => void>();

function restore(): FocusEntity | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as FocusEntity | null;
    return saved?.id && saved.name ? saved : null;
  } catch {
    return null;
  }
}

let current = restore();

export function focusEntity(): FocusEntity | null {
  return current;
}

export function setFocusEntity(next: FocusEntity | null): void {
  if (current?.id === next?.id) return;
  current = next;
  try {
    if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sem armazenamento, a escolha vale até recarregar a página.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useFocusEntity(): FocusEntity | null {
  return useSyncExternalStore(subscribe, focusEntity);
}

/** Troca a empresa: o que estava carregado era de outra visão, e tudo é buscado de novo. */
export function reloadDataOnFocusChange(client: QueryClient) {
  return subscribe(() => {
    void client.resetQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
  });
}
